import React from 'react';
import { Tooltip, TooltipTrigger } from '@chatsdet/ui';
import type {
  ExternalRunProgressSnapshot,
  ExternalRunStreamStatus,
} from '@chatsdet/types';
import type {
  ExternalRunProgressState,
  ExternalShardProgress,
} from '../../../features/external-tests/externalRunProgressModel';

const PHASES: { id: string; label: string; description: string }[] = [
  { id: 'queued', label: 'Queued', description: 'Waiting to start' },
  { id: 'running', label: 'Running', description: 'Executing shards' },
  { id: 'collecting', label: 'Collecting', description: 'Gathering results' },
  { id: 'sealed', label: 'Done', description: 'Run complete' },
];

function phaseIndex(status: ExternalRunStreamStatus | null): number {
  switch (status) {
    case 'sealed':
      return 3;
    case 'collecting':
      return 2;
    case 'running':
      return 1;
    case 'queued':
    default:
      return 0;
  }
}

function shardSummary(shards: readonly ExternalShardProgress[], total: number): string {
  const completed = shards.filter((shard) => shard.phase === 'done').length;
  if (total <= 0) return 'Preparing shards';
  return `${completed} of ${total} shard${total === 1 ? '' : 's'} complete`;
}

function formatElapsed(startedAt: string | null | undefined, now: number): string | null {
  if (!startedAt) return null;
  const elapsedSeconds = Math.max(0, Math.floor((now - Date.parse(startedAt)) / 1000));
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s elapsed` : `${seconds}s elapsed`;
}

/**
 * Footer content for `RunTriageLockup` while an external run is still live —
 * a subtle info line plus a thin phase rail flush along the card's bottom
 * edge. Returns null once the run is finished (the poll's `isRunFinished` is
 * authoritative) or the stream isn't live, so the footer simply disappears
 * rather than rendering a stale "collecting" state.
 */
export function ExternalRunProgress({
  stream,
  progress,
  revision,
  targetUrl,
  isRunFinished,
}: {
  stream: ExternalRunProgressState;
  progress: ExternalRunProgressSnapshot | null;
  revision: string | null;
  targetUrl: string | null;
  /** From the poll, which is the authority on whether the run is over. */
  isRunFinished: boolean;
}) {
  const [now, setNow] = React.useState(() => Date.now());

  const startedAt = progress?.run.startedAt;
  React.useEffect(() => {
    if (!startedAt || isRunFinished) return;
    const timer = window.setInterval(() => setNow(Date.now()), 10_000);
    return () => window.clearInterval(timer);
  }, [isRunFinished, startedAt]);

  if (isRunFinished || !stream.isLive) return null;

  const total = stream.shardTotal ?? stream.shards.length;
  const relaunches = progress?.summary.relaunchCount ?? 0;
  const testRetries = progress?.summary.testRetryCount ?? 0;
  const timeouts = progress?.summary.timeoutCount ?? 0;
  const elapsed = formatElapsed(startedAt, now);
  const current = phaseIndex(stream.status);

  // Only progress is announced; the elapsed clock re-renders every 10s.
  const liveParts: string[] = [shardSummary(stream.shards, total)];
  if (relaunches > 0) liveParts.push(`${relaunches} relaunch${relaunches === 1 ? '' : 'es'}`);
  if (testRetries > 0) liveParts.push(`${testRetries} test retr${testRetries === 1 ? 'y' : 'ies'}`);
  if (timeouts > 0) liveParts.push(`${timeouts} timeout${timeouts === 1 ? '' : 's'}`);
  const staticParts: string[] = [];
  if (elapsed) staticParts.push(elapsed);
  if (revision) staticParts.push(revision.slice(0, 7));
  if (targetUrl) staticParts.push(targetUrl);

  return (
    <div>
      <div className="border-t border-border px-4 pb-2 pt-2 text-xs text-muted-foreground truncate">
        <span aria-live="polite">{liveParts.join(' · ')}</span>
        {staticParts.map((part) => ` · ${part}`).join('')}
      </div>
      <div
        role="progressbar"
        aria-label="External run phases"
        aria-valuemin={0}
        aria-valuemax={PHASES.length - 1}
        aria-valuenow={current}
        aria-valuetext={PHASES[current]?.label ?? 'Queued'}
        className="-mt-2 flex gap-[2px]"
      >
        {PHASES.map((phase, index) => {
          const isPast = index < current;
          const isCurrent = index === current;
          return (
            <TooltipTrigger key={phase.id} delay={0} offset={6} placement="top">
              {/* A 3px bar is too thin to hover; the transparent 8px cell above it is the target. */}
              <span className="flex h-2 flex-1 items-end">
                <span
                  className={`h-[3px] w-full ${
                    isPast
                      ? 'bg-primary'
                      : isCurrent
                        ? 'bg-primary/60 animate-pulse'
                        : 'bg-border'
                  }`}
                />
              </span>
              <Tooltip variant="light" className="px-2 py-1 text-xs">
                {phase.label} — {phase.description}
                {isCurrent ? ' (current)' : ''}
              </Tooltip>
            </TooltipTrigger>
          );
        })}
      </div>
    </div>
  );
}
